/**
 * «Список с рукописи» (Manuscript copy, architecture §4.3): `'MC1.' + base64url(utf8(JSON.stringify(envelope))) + '.' +
 * checksum`. No compression in P0; `MC2.` (deflate-raw) is P1 and rejected here with a clear message.
 */
import type { SaveEnvelope, SaveState } from '../../shared';
import { checksumOf, parseEnvelope, wrapEnvelope } from './envelope';

const toB64Url = (bytes: Uint8Array): string => {
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
const fromB64Url = (s: string): Uint8Array => {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};

/** Encodes a save as an `MC1.` copy string. */
export function encodeSaveCode(state: SaveState, savedAt: number): string {
  const env = wrapEnvelope(state, savedAt);
  const json = JSON.stringify(env);
  return `MC1.${toB64Url(new TextEncoder().encode(json))}.${checksumOf(env.data)}`;
}

export type DecodeResult = { ok: true; envelope: SaveEnvelope } | { ok: false; error: string };

/** Decodes an `MC1.` string (or a raw envelope / SaveState JSON, for debug convenience). */
export function decodeSaveCode(code: string): DecodeResult {
  const s = code.trim();
  if (s.startsWith('{')) {
    try {
      const obj = JSON.parse(s) as Partial<SaveEnvelope> & Partial<SaveState>;
      if (obj.format === 'marg-save') {
        const p = parseEnvelope(s);
        return p.ok ? { ok: true, envelope: p.envelope } : { ok: false, error: p.error };
      }
      return { ok: true, envelope: wrapEnvelope(obj as SaveState, 0) };
    } catch (e) { return { ok: false, error: `json: ${(e as Error).message}` }; }
  }
  if (s.startsWith('MC2.')) return { ok: false, error: 'MC2 (compressed) copies are not supported by this build' };
  const m = /^MC1\.([A-Za-z0-9_-]+)\.([0-9a-f]{8})$/.exec(s);
  if (!m) return { ok: false, error: 'not a manuscript copy (expected MC1.…)' };
  let json: string;
  try { json = new TextDecoder().decode(fromB64Url(m[1])); } catch (e) { return { ok: false, error: `base64: ${(e as Error).message}` }; }
  const p = parseEnvelope(json);
  if (!p.ok) return { ok: false, error: p.error };
  let rawChecksum: unknown;
  try { rawChecksum = (JSON.parse(json) as { checksum?: unknown }).checksum; } catch { rawChecksum = null; }
  if (rawChecksum !== m[2]) return { ok: false, error: 'copy checksum mismatch' };
  return { ok: true, envelope: p.envelope };
}
